'use client'

import { format, fromUnixTime } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

interface DateWrapperProps {
  children: Date | string | number | undefined
  timezone?: string
  formatStr?: string
  className?: string
}

export default function DateWrapper({ children, timezone = 'UTC', formatStr = 'PPP', className }: DateWrapperProps) {
  const formatDate = (value: Date | string | number | undefined): string => {
    if (!value) return ''

    try {
      let dateValue: Date

      if (value instanceof Date) {
        dateValue = value
      } else if (typeof value === 'number') {
        // Handle Unix timestamp (in seconds)
        if (value < 1e12) {
          dateValue = fromUnixTime(value)
        } else {
          // Handle Unix timestamp (in milliseconds)
          dateValue = new Date(value)
        }
      } else if (typeof value === 'string') {
        if (!isNaN(Number(value))) {
          // Handle numeric string timestamps
          const numValue = Number(value)
          if (numValue < 1e12) {
            dateValue = fromUnixTime(numValue)
          } else {
            dateValue = new Date(numValue)
          }
        } else {
          // Handle ISO string or other date string formats
          dateValue = new Date(value)
        }
      } else {
        return ''
      }

      // Check if the date is valid
      if (isNaN(dateValue.getTime())) {
        return ''
      }

      if (timezone) {
        return formatInTimeZone(dateValue, timezone, formatStr)
      } else {
        return format(dateValue, formatStr)
      }
    } catch (error) {
      console.error('Error formatting date:', error)
      return ''
    }
  }

  return (
    <span className={className} suppressHydrationWarning>
      {formatDate(children)}
    </span>
  )
}

// Example usage:
// Add this to your documentation or as a comment
/*
import { DateWrapper } from '@/components/date-wrapper';

// Different ways to use the component:
<DateWrapper>2024-03-14T12:00:00Z</DateWrapper>
<DateWrapper timezone="America/New_York">2024-03-14T12:00:00Z</DateWrapper>
<DateWrapper formatStr="PPpp">2024-03-14T12:00:00Z</DateWrapper>
<DateWrapper>{1710417600}</DateWrapper>
<DateWrapper>{new Date()}</DateWrapper>
<DateWrapper>{undefined}</DateWrapper>

// Format string examples:
// 'PP' -> "Mar 14, 2024"
// 'PPP' -> "March 14th, 2024"
// 'PPPP' -> "Thursday, March 14th, 2024"
// 'p' -> "12:00 PM"
// 'pp' -> "12:00:00 PM"
// 'ppp' -> "12:00:00 PM GMT+0"
// 'PPpp' -> "March 14th, 2024, 12:00:00 PM"
*/
